import { html, SP } from '../../html.js';
import type { VNode } from '../../html.js';
import type { Overview } from '../api.js';
import type { DashboardActions, DashboardState } from '../store.js';
import { Empty, ErrorNote, ErrorState, Scroller, Skeleton, Verdict, When, fmtInt } from '../ui/bits.js';
import { CrawlForm } from './onboard.js';

/**
 * Sites: the ones this account owns, or the ones this host serves.
 *
 * It was called Fleet, which is operator vocabulary. A customer with one site
 * does not have a fleet and will not go looking for one; the operator, who does
 * have a fleet, is the person least likely to be confused by the word "sites".
 * So the customer's word wins, and every empty state and heading follows it.
 *
 * WHAT MOVED OFF THIS SCREEN. The dark tenants, the host-wide call total and
 * the "Operator console" eyebrow are operator furniture and now live at
 * `#/host`. What is left when an operator opens this is a one-line alert saying
 * how many manifests failed to load, with a link — the fact is too expensive to
 * hide and the block is too big to sit on a customer's first screen.
 *
 * FOUR COLUMNS. It carried eight — site, health, faults, tools, calls, last
 * checked, ownership and origin — and a customer with three sites was reading a
 * spreadsheet to answer "is it up". The two that had to survive are honesty
 * columns and they did, folded into health rather than dropped: a verdict that
 * says "never checked" where no probe has run instead of a hopeful tick, and a
 * fault count beside it, because a tool advertising a parameter its binding
 * cannot honour is the one defect that answers confidently and wrongly.
 * Ownership and origin moved to the site itself, which is where you go when the
 * answer to "is it up" is no.
 *
 * WHAT THE SCREEN SAYS. A count, a call total, a button, a table. The faults
 * sentence is printed only when there ARE faults — the old screen spent a line
 * every visit saying that nothing was wrong, which trains a person to skip the
 * one place the word "fault" ever appears. The filter box waits until the list
 * is longer than fits in a glance, because a search field over three rows is a
 * control that costs more attention than the scan it replaces. And the note
 * explaining which file keeps the call counters was written for me, not for the
 * person paying for the calls.
 *
 * The list is whatever the scoped route returned. The operator sees the host;
 * an account sees the tenants it is a member of, and that line is drawn by the
 * SERVER — nothing here compares a row against `principal.tenants`, because a
 * client-side filter is a second copy of an authorisation rule, and the day the
 * two disagree the screen would be the one that is wrong. The filter box above
 * the table is a different thing entirely: it narrows a list already on screen
 * and never asks the server for anything.
 */

/**
 * The first run: an invitation, a field, and what the field will cost.
 *
 * This is the moment the product is judged and it used to judge badly. A
 * brand-new account read `0 sites in this account` above an empty table, a
 * paragraph, and three numbered steps whose links all went to the public
 * console — away from the signed-in page, to a screen that cannot do steps two
 * or three at all.
 *
 * Four things changed and each of them is a rule rather than a decoration.
 *
 * THE HEADING IS THE INVITATION, NOT THE COUNT. Zero is a true number and a
 * terrible first sentence; it is a fact about an empty table, and there is no
 * table here to be a fact about.
 *
 * THE FORM IS INLINE. The first action has to be possible without navigating,
 * so this is the crawl form itself — `CrawlForm` from the crawl screen, one
 * copy, same submit — and not a link to it.
 *
 * THE THREE STEPS ARE A PREVIEW, NOT LINKS. Verify and publish cannot be
 * started from anywhere yet; they appear at the end of a crawl. Linking them
 * would be three doors, two of which are painted on. Each carries the one
 * sentence that says what it costs.
 *
 * THE EXPECTATION IS HONEST AND IS THE CODE'S OWN. The result of a crawl is a
 * draft; most sites yield a handful of tools; the gate refusing to ship a tool
 * it could not verify is the product working. The public console has said this
 * on every barren result since the gate existed, and the dashboard never did.
 */
function FirstRun(props: { state: DashboardState; actions: DashboardActions; now: number }): VNode {
  const { state, actions, now } = props;
  // A crawl already started and not yet published is the one thing that would
  // make this screen a lie, and it is the ordinary case for somebody who came
  // back the next morning: the job list is read here for exactly that. A list
  // that failed to load is said out loud at the foot for the same reason — an
  // absent list and an empty one are the same screen otherwise — while the form
  // above goes on working, which is why it is a note rather than the screen.
  const open = (state.onboard.jobs ?? []).filter((j) => !j.publishedAt);

  return html`
    <div class="firstrun">
      <h1 class="firstrun__h" tabindex="-1" data-focus-target>
        Make your site answerable by agents.
      </h1>
      <p class="firstrun__lede">
        Paste your address. The crawler reads the site — its sitemap, its structured data, the pages
        people actually use — and assembles read-only tools from what it can verify against the live
        origin. Publishing serves them at an MCP endpoint any client can connect to, and at a page
        of your own.
      </p>

      ${CrawlForm({ state, actions })}

      ${open.length
        ? html`<div class="firstrun__open">
            <p class="firstrun__open-h">
              ${open.length === 1 ? 'A crawl you started is waiting.' : `${open.length} crawls you started are waiting.`}
            </p>
            <ul class="firstrun__jobs">
              ${open.map(
                (j) => html`<li class="firstrun__job">
                  <a class="linkish" href=${`#/crawl/${encodeURIComponent(j.id)}`}>${j.url}</a>${SP}
                  <span class="firstrun__job-state">${j.draft ? 'draft — ready to publish' : j.state}</span>${SP}
                  <span class="firstrun__job-when">${When({ iso: j.createdAt, now, absent: '' })}</span>
                </li>`,
              )}
            </ul>
          </div>`
        : null}
      ${ErrorNote({
        message: state.onboard.jobsError,
        label: 'Could not check whether you have a crawl already running.',
      })}

      <ol class="steps firstrun__steps">
        <li class="step">
          <b class="step__h">Crawl.</b> Minutes, not seconds — the crawler asks the site's
          <code>robots.txt</code> first and obeys the delay it asks for. It ends with a draft, and a
          draft is never served.
        </li>
        <li class="step">
          <b class="step__h">Prove the domain.</b> One DNS TXT record, one file at a fixed path, or
          one meta tag in the home page's <code>${'<head>'}</code>. Any one of the three, checked
          against the live site. Only your own domain needs this, and only to publish without the
          "Unofficial" disclaimer.
        </li>
        <li class="step">
          <b class="step__h">Publish.</b> Two URLs start answering — a page and an MCP endpoint —
          and they keep answering until you take them down from this dashboard.
        </li>
      </ol>

      <p class="firstrun__honest">
        What to expect from the first crawl: a draft, and usually a handful of tools rather than a
        catalogue. Every proposal is executed against your own origin before it ships, and one that
        returns nothing usable is turned back with the reason recorded. That gate is the thing being
        paid for — a tool that cannot be demonstrated to work is worse than no tool, because an
        agent will call it anyway.
      </p>
    </div>
  `;
}

export function SitesScreen(props: {
  state: DashboardState;
  actions: DashboardActions;
  now: number;
}): VNode {
  const { state, actions, now } = props;
  const account = state.principal?.kind === 'account';

  if (state.error) {
    return html`
      <section class="wrap screen">
        <h1 class="screen__h" tabindex="-1" data-focus-target>Sites</h1>
        ${ErrorState({
          message: state.error,
          label: 'Could not load your sites.',
          body: 'Nothing below this line is a fallback: this page has no recorded data to replay, so an unreachable API shows as this message and no table. Start the server that serves /api/dashboard/overview and try again.',
          onRetry: () => actions.reload(),
        })}
      </section>
    `;
  }

  if (!state.overview) {
    return html`
      <section class="wrap screen">
        <h1 class="screen__h" tabindex="-1" data-focus-target>Sites</h1>
        ${Skeleton({ rows: 5, label: 'Loading your sites…' })}
      </section>
    `;
  }

  const o: Overview = state.overview;

  // Before anything else, because the first run is a different screen and not
  // an empty state inside this one. A count, a filter box and a table header
  // over nothing are three pieces of furniture for a person who has not yet
  // been given the one thing they came for.
  if (account && o.tenants.length === 0) {
    return html`<section class="wrap screen">${FirstRun({ state, actions, now })}</section>`;
  }

  const faults = o.tenants.reduce((n, t) => n + t.issues, 0);
  const needle = state.siteFilter.trim().toLowerCase();
  const rows = needle
    ? o.tenants.filter(
        (t) =>
          t.tenantId.toLowerCase().includes(needle) ||
          t.displayName.toLowerCase().includes(needle) ||
          (t.origin ?? '').toLowerCase().includes(needle),
      )
    : o.tenants;

  return html`
    <section class="wrap screen">
      <div class="screen__top">
        <div>
          <h1 class="screen__h" tabindex="-1" data-focus-target>
            ${o.tenants.length} ${o.tenants.length === 1 ? 'site' : 'sites'}
          </h1>
          <p class="screen__meta">
            ${fmtInt(o.usage.calls)} ${o.usage.calls === 1 ? 'call' : 'calls'}${o.usage.from && o.usage.to
              ? `, ${o.usage.from} to ${o.usage.to}`
              : ''}
          </p>
        </div>
        <a class="btn" href="#/crawl">Add a site</a>
      </div>

      ${faults
        ? html`<p class="callout callout--warn" role="alert">
            ${faults} ${faults === 1 ? 'tool advertises' : 'tools advertise'} a parameter its
            binding cannot honour, so it answers confidently and wrongly.${SP}
            ${faults === 1 ? 'The affected site is' : 'The affected sites are'} marked below.
          </p>`
        : null}

      ${!account && o.failed.length
        ? html`<p class="callout callout--warn" role="alert">
            ${o.failed.length} ${o.failed.length === 1 ? 'manifest is' : 'manifests are'} on disk
            and did not load, so ${o.failed.length === 1 ? 'it has' : 'they have'} no registry entry
            and ${o.failed.length === 1 ? 'is' : 'are'} absent from the table below.${SP}
            <a href="#/host">See what failed</a>
          </p>`
        : null}

      ${o.tenants.length > 6
        ? html`<div class="filter">
            <label class="cardlabel" for="site-filter">Filter</label>
            <input
              id="site-filter"
              class="filter__input"
              type="search"
              placeholder="name, id or origin"
              value=${state.siteFilter}
              onInput=${(e: Event) => actions.setSiteFilter((e.target as HTMLInputElement).value)}
            />
          </div>`
        : null}

      ${o.tenants.length
        ? rows.length
          ? Scroller({
              label: 'Sites',
              children: html`
                <table class="fleet">
                  <thead>
                    <tr>
                      <th scope="col">Site</th>
                      <th scope="col">Health</th>
                      <th scope="col" class="num">Tools</th>
                      <th scope="col" class="num">Calls</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${rows.map(
                      (t) => html`
                        <tr class="fleet__row">
                          <th scope="row">
                            <a class="fleet__open" href=${`#/sites/${encodeURIComponent(t.tenantId)}`}>
                              <span class="fleet__name">${t.displayName}</span>
                              <span class="fleet__id">${t.origin || t.tenantId}</span>
                            </a>
                          </th>
                          <td>
                            ${Verdict({ verdict: t.verdict, checkedAt: t.lastCheckedAt })}
                            ${t.issues
                              ? html`<span class="fault"
                                  >${t.issues} fault${t.issues === 1 ? '' : 's'}</span
                                >`
                              : null}
                          </td>
                          <td class="num">${t.tools}</td>
                          <td class="num">${fmtInt(t.calls)}</td>
                        </tr>
                      `,
                    )}
                  </tbody>
                </table>
              `,
            })
          : Empty({
              title: `Nothing matches "${state.siteFilter.trim()}".`,
              body: `The filter narrows the ${o.tenants.length} ${o.tenants.length === 1 ? 'site' : 'sites'} already loaded; it never asks the server for more. Clear it to see them all.`,
            })
        : Empty({
            // Only the operator reaches this: an account with no sites was sent
            // to the first-run screen above before the table was ever built.
            title: 'No site is published on this host.',
            body: 'The registry is empty, which is what a fresh host looks like. A crawl is where the first one comes from.',
            action: { label: 'Add a site', href: '#/crawl' },
          })}

    </section>
  `;
}
